<template>
  <section
    id="reconciliation-compliance-tracker"
    class="recon-dashboard"
    v-if="$can('read','invoice')"
  >
    <!-- Compact toolbar (page title lives in navbar PageTitle) -->
    <div class="recon-dash-header">
      <div class="recon-dash-header__meta">
        <span class="recon-period-label">
          <feather-icon icon="CalendarIcon" size="14" class="mr-50" />
          {{ periodLabel }}
        </span>
        <b-badge v-if="usingFallback" variant="light-warning" class="recon-source-badge">
          Fallback data
        </b-badge>
      </div>
      <div class="recon-dash-header__actions">
        <b-button
          variant="outline-secondary"
          size="sm"
          class="btn-icon"
          aria-label="Refresh"
          :disabled="loading"
          @click="loadData"
        >
          <feather-icon icon="RefreshCwIcon" size="15" :class="{ 'recon-spin': loading }" />
        </b-button>
        <b-button
          variant="outline-secondary"
          size="sm"
          class="btn-icon"
          aria-label="Export"
          @click="showExportModal"
        >
          <feather-icon icon="DownloadIcon" size="15" />
        </b-button>
        <b-button
          variant="outline-primary"
          size="sm"
          :to="{ name: 'apps-invoice-dashboard' }"
        >
          <feather-icon icon="ArrowLeftIcon" size="15" class="mr-50" />
          <span class="d-none d-sm-inline">Invoice Tracker</span>
        </b-button>
      </div>
    </div>

    <!-- Filters -->
    <div class="recon-filter-toolbar mb-2">
      <div class="recon-filter-toolbar__primary">
        <div class="recon-filter-field">
          <label>From</label>
          <flat-pickr
            class="form-control form-control-sm"
            v-model="filters.startDate"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
          />
        </div>
        <div class="recon-filter-field">
          <label>To</label>
          <flat-pickr
            class="form-control form-control-sm"
            v-model="filters.endDate"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
          />
        </div>
        <div class="recon-filter-field recon-filter-field--grow d-none d-md-block">
          <label>Client</label>
          <v-select
            v-model="filters.client"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            label="title"
            :options="clients"
            placeholder="All clients"
            :reduce="(option) => option.title"
            :clearable="true"
            class="recon-vselect"
          />
        </div>
        <div class="recon-filter-field recon-filter-field--actions">
          <label class="d-none d-md-block">&nbsp;</label>
          <div class="d-flex flex-wrap align-items-center">
            <b-button variant="primary" size="sm" class="mr-50 mb-50 mb-md-0" :disabled="loading" @click="applyFilters">
              Apply
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="mr-50 mb-50 mb-md-0"
              :disabled="!hasActiveFilters"
              @click="clearFilters"
            >
              Clear
            </b-button>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="mb-50 mb-md-0"
              @click="filtersExpanded = !filtersExpanded"
            >
              <feather-icon :icon="filtersExpanded ? 'ChevronUpIcon' : 'SlidersIcon'" size="14" class="mr-25" />
              More
              <b-badge v-if="advancedFilterCount" variant="primary" class="ml-25 recon-filter-count">
                {{ advancedFilterCount }}
              </b-badge>
            </b-button>
          </div>
        </div>
      </div>

      <div class="recon-booking-chips mt-1">
        <button
          v-for="chip in bookingQuickFilters"
          :key="chip.value === null ? 'all' : chip.value"
          type="button"
          class="recon-chip"
          :class="{ 'is-active': filters.bookingStatus === chip.value }"
          @click="setBookingQuickFilter(chip.value)"
        >
          {{ chip.label }}
          <span v-if="chip.count != null" class="recon-chip__count">{{ chip.count }}</span>
        </button>
      </div>

      <b-collapse :visible="filtersExpanded">
        <div class="recon-filters-advanced">
          <b-row>
            <b-col cols="12" sm="6" md="4" class="d-md-none mb-1">
              <b-form-group label="Client" class="mb-0">
                <v-select
                  v-model="filters.client"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  label="title"
                  :options="clients"
                  placeholder="All clients"
                  :reduce="(option) => option.title"
                  :clearable="true"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="4" class="mb-1">
              <b-form-group label="Status" class="mb-0">
                <v-select
                  v-model="filters.status"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  label="title"
                  :options="statusOptions"
                  placeholder="All statuses"
                  :reduce="(option) => option.title"
                  :clearable="true"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6" md="4" class="mb-1">
              <b-form-group label="Category" class="mb-0">
                <v-select
                  v-model="filters.category"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  label="title"
                  :options="categoryOptions"
                  placeholder="All categories"
                  :reduce="(option) => option.title"
                  :clearable="true"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </b-collapse>
    </div>

    <!-- Error / empty banner -->
    <b-alert
      v-if="loadError"
      show
      variant="danger"
      class="recon-alert"
      dismissible
      @dismissed="loadError = ''"
    >
      <div class="d-flex align-items-start">
        <feather-icon icon="AlertCircleIcon" size="18" class="mr-75 mt-25" />
        <div>
          <strong class="d-block">Couldn’t load reconciliation data</strong>
          <span class="small">{{ loadError }}</span>
        </div>
      </div>
    </b-alert>

    <!-- Detail workspace -->
    <b-card class="recon-workspace mb-0" body-class="p-0">
      <div class="recon-workspace__toolbar px-1 px-md-2 pt-1">
        <div class="recon-workspace__search">
          <b-input-group size="sm">
            <template #prepend>
              <b-input-group-text class="recon-search-icon">
                <feather-icon icon="SearchIcon" size="14" />
              </b-input-group-text>
            </template>
            <b-form-input
              v-model="searchQuery"
              placeholder="Search in current tab…"
              :debounce="200"
            />
            <template v-if="searchQuery" #append>
              <b-button size="sm" variant="outline-secondary" @click="searchQuery = ''">
                <feather-icon icon="XIcon" size="14" />
              </b-button>
            </template>
          </b-input-group>
        </div>
        <div class="recon-workspace__meta">
          <span v-if="loading">Loading…</span>
          <span v-else>{{ activeTabRowCount }} row{{ activeTabRowCount === 1 ? '' : 's' }}</span>
        </div>
      </div>

      <div class="recon-workspace__tabs px-1 px-md-2 pb-1">
        <b-tabs v-model="activeTab" lazy nav-class="recon-dash-tabs" content-class="mt-1">
          <b-tab title="Invoice Overview">
            <recon-invoice-overview-tab
              :items="filteredInvoiceOverviewData"
              :fields="invoiceOverviewFields"
              :loading="loading"
              :per-page="perPage"
              :current-page="currentPage"
              :total-rows="filteredInvoiceOverviewData.length"
              :search-active="!!searchQuery"
              @view-invoice="viewInvoiceDetails"
              @update-booking="updateBookingStatus"
              @page-change="currentPage = $event"
              @clear-search="searchQuery = ''"
            />
          </b-tab>
          <b-tab title="Financial">
            <recon-financial-tab
              :items="filteredFinancialData"
              :fields="financialFields"
              :loading="loading"
              :per-page="perPage"
              :current-page="currentPage"
              :total-rows="filteredFinancialData.length"
              :search-active="!!searchQuery"
              @page-change="currentPage = $event"
              @clear-search="searchQuery = ''"
            />
          </b-tab>
          <b-tab title="Tax">
            <recon-tax-tab
              :items="filteredTaxData"
              :fields="taxFields"
              :loading="loading"
              :per-page="perPage"
              :current-page="currentPage"
              :total-rows="filteredTaxData.length"
              :search-active="!!searchQuery"
              @page-change="currentPage = $event"
              @clear-search="searchQuery = ''"
            />
          </b-tab>
          <b-tab title="Compliance">
            <recon-compliance-tab
              :items="filteredComplianceData"
              :fields="complianceFields"
              :loading="loading"
              :per-page="perPage"
              :current-page="currentPage"
              :total-rows="filteredComplianceData.length"
              :search-active="!!searchQuery"
              @page-change="currentPage = $event"
              @update-einvoice="openEInvoiceModal"
              @clear-search="searchQuery = ''"
            />
          </b-tab>
          <b-tab title="Audit Trail">
            <recon-audit-trail-tab
              :items="filteredAuditTrailData"
              :fields="auditTrailFields"
              :loading="loading"
              :per-page="perPage"
              :current-page="currentPage"
              :total-rows="filteredAuditTrailData.length"
              :search-active="!!searchQuery"
              @page-change="currentPage = $event"
              @clear-search="searchQuery = ''"
            />
          </b-tab>
        </b-tabs>
      </div>
    </b-card>

    <!-- Export Modal -->
    <InvoiceReconciliationExport
      ref="exportModal"
      :filters="filters"
    />

    <!-- Booking Status Modal -->
    <BookingStatusModal
      ref="bookingStatusModal"
      :invoice="selectedInvoice"
      @booking-status-updated="handleBookingStatusUpdated"
    />

    <!-- E-Invoice Status Modal -->
    <e-invoice-status-modal
      ref="eInvoiceStatusModal"
      @save="saveEInvoiceStatus"
    />
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BFormGroup,
  BFormInput,
  BBadge,
  BTabs,
  BTab,
  BAlert,
  BCollapse,
  BInputGroup,
  BInputGroupText
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import moment from 'moment'
import invoiceService from '@/services/invoice-service'
import clientService from '@/services/client-service'
import enhancedInvoiceService from '@/services/enhanced-invoice-service'
import InvoiceReconciliationExport from './components/modals/InvoiceReconciliationExport.vue'
import BookingStatusModal from './components/modals/BookingStatusModal.vue'
import EInvoiceStatusModal from './components/modals/EInvoiceStatusModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ReconInvoiceOverviewTab from './components/recon-tabs/ReconInvoiceOverviewTab.vue'
import ReconFinancialTab from './components/recon-tabs/ReconFinancialTab.vue'
import ReconTaxTab from './components/recon-tabs/ReconTaxTab.vue'
import ReconComplianceTab from './components/recon-tabs/ReconComplianceTab.vue'
import ReconAuditTrailTab from './components/recon-tabs/ReconAuditTrailTab.vue'
import { resolveInvoiceApiKey } from '@/utils/invoice-api-key'

export default {
  name: 'InvoiceReconciliation',
  components: {
    BRow, BCol, BCard, BButton, BFormGroup, BFormInput, BBadge,
    BTabs, BTab, BAlert, BCollapse, BInputGroup, BInputGroupText,
    vSelect, flatPickr,
    InvoiceReconciliationExport, BookingStatusModal, EInvoiceStatusModal, ToastificationContent,
    ReconInvoiceOverviewTab, ReconFinancialTab, ReconTaxTab,
    ReconComplianceTab, ReconAuditTrailTab
  },
  data() {
    return {
      loading: false,
      loadError: '',
      usingFallback: false,
      filtersExpanded: false,
      activeTab: 0,
      perPage: 10,
      currentPage: 1,
      searchQuery: '',
      filters: {
        startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        status: null,
        client: null,
        category: null,
        bookingStatus: null
      },
      statistics: {
        totalInvoices: 0,
        totalRevenue: 0,
        pendingAmount: 0,
        paidAmount: 0,
        overdueInvoices: 0,
        bookedInvoices: 0,
        notBookedInvoices: 0,
        eInvoiceFiled: 0
      },
      invoiceOverviewData: [],
      financialData: [],
      taxData: [],
      complianceData: [],
      auditTrailData: [],
      clients: [],
      statusOptions: [
        { title: 'Draft' },
        { title: 'Pending Approval' },
        { title: 'Approved' },
        { title: 'Issued' },
        { title: 'Raised' },
        { title: 'Part Paid' },
        { title: 'Paid' },
        { title: 'Unpaid' },
        { title: 'Overdue' },
        { title: 'Disputed' },
        { title: 'Cancelled' },
        { title: 'Revised' },
        { title: 'Early Attrition' }
      ],
      categoryOptions: [
        { title: 'Recruitment' },
        { title: 'Training' },
        { title: 'Anti Fraud' },
        { title: 'Contractual' },
        { title: 'Payroll' },
        { title: 'BGV' },
        { title: 'Miscellanous' }
      ],
      invoiceOverviewFields: [
        { key: 'invoice_id', label: 'Invoice', sortable: true, thClass: 'text-nowrap', tdClass: 'text-nowrap' },
        { key: 'client', label: 'Client', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'total_amount', label: 'Total Amount', sortable: true },
        { key: 'booking_status', label: 'Booking Status', sortable: true },
        { key: 'actions', label: 'Actions', thClass: 'text-right', tdClass: 'text-right' }
      ],
      financialFields: [
        { key: 'invoice_id', label: 'Invoice', sortable: true, thClass: 'text-nowrap', tdClass: 'text-nowrap' },
        { key: 'client', label: 'Client', sortable: true },
        { key: 'base_amount', label: 'Base Amount', sortable: true },
        { key: 'total_amount', label: 'Total Amount', sortable: true },
        { key: 'paid_amount', label: 'Paid Amount', sortable: true },
        { key: 'pending_amount', label: 'Pending Amount', sortable: true }
      ],
      taxFields: [
        { key: 'invoice_id', label: 'Invoice', sortable: true, thClass: 'text-nowrap', tdClass: 'text-nowrap' },
        { key: 'gst_type', label: 'GST Type', sortable: true },
        { key: 'total_gst', label: 'Total GST', sortable: true },
        { key: 'tds_amount', label: 'TDS', sortable: true }
      ],
      complianceFields: [
        { key: 'invoice_id', label: 'Invoice', sortable: true, thClass: 'text-nowrap', tdClass: 'text-nowrap' },
        { key: 'client_gst', label: 'Client GST', sortable: true },
        { key: 'sattva_gst', label: 'Sattva GST', sortable: true },
        { key: 'einvoice_filed', label: 'E-Invoice Filed', sortable: true },
        { key: 'einvoice_date', label: 'E-Invoice Date', sortable: true },
        { key: 'tds_applicable', label: 'TDS Applicable', sortable: true },
        { key: 'tds_percentage', label: 'TDS %', sortable: true }
      ],
      auditTrailFields: [
        { key: 'timestamp', label: 'Timestamp', sortable: true },
        { key: 'action', label: 'Action', sortable: true },
        { key: 'user', label: 'User', sortable: true },
        { key: 'field', label: 'Field', sortable: true },
        { key: 'old_value', label: 'Old Value', sortable: true },
        { key: 'new_value', label: 'New Value', sortable: true }
      ],
      selectedInvoice: null
    }
  },
  computed: {
    periodLabel() {
      const start = this.filters.startDate ? moment(this.filters.startDate).format('DD MMM YYYY') : '—'
      const end = this.filters.endDate ? moment(this.filters.endDate).format('DD MMM YYYY') : '—'
      return `${start} – ${end}`
    },
    hasActiveFilters() {
      const defaultStart = moment().subtract(30, 'days').format('YYYY-MM-DD')
      const defaultEnd = moment().format('YYYY-MM-DD')
      return !!(
        this.filters.status ||
        this.filters.client ||
        this.filters.category ||
        this.filters.bookingStatus ||
        this.searchQuery ||
        this.filters.startDate !== defaultStart ||
        this.filters.endDate !== defaultEnd
      )
    },
    advancedFilterCount() {
      let n = 0
      if (this.filters.status) n += 1
      if (this.filters.category) n += 1
      return n
    },
    bookingQuickFilters() {
      // Counts only when no booking filter is applied (stats are computed post-filter)
      const showCounts = !this.filters.bookingStatus
      const total = this.statistics.totalInvoices || 0
      const booked = this.statistics.bookedInvoices || 0
      const notBooked = this.statistics.notBookedInvoices || 0
      const pending = Math.max(0, total - booked - notBooked)
      return [
        { label: 'All', value: null, count: showCounts ? (total || null) : null },
        { label: 'Booked', value: 'Booked', count: showCounts ? (booked || null) : null },
        { label: 'Not Booked', value: 'Not Booked', count: showCounts ? (notBooked || null) : null },
        { label: 'Pending', value: 'Pending', count: showCounts ? (pending || null) : null }
      ]
    },
    activeTabRowCount() {
      const map = [
        this.filteredInvoiceOverviewData,
        this.filteredFinancialData,
        this.filteredTaxData,
        this.filteredComplianceData,
        this.filteredAuditTrailData
      ]
      const rows = map[this.activeTab] || []
      return rows.length
    },
    filteredInvoiceOverviewData() {
      return this.filterTableBySearch(this.invoiceOverviewData, ['invoice_id', 'invoice_date', 'client', 'status', 'total_amount', 'booking_status'])
    },
    filteredFinancialData() {
      return this.filterTableBySearch(this.financialData, ['invoice_id', 'invoice_date', 'client', 'base_amount', 'total_amount'])
    },
    filteredTaxData() {
      return this.filterTableBySearch(this.taxData, ['invoice_id', 'invoice_date', 'gst_type', 'total_gst'])
    },
    filteredComplianceData() {
      return this.filterTableBySearch(this.complianceData, ['invoice_id', 'invoice_date', 'client_gst', 'sattva_gst'])
    },
    filteredAuditTrailData() {
      return this.filterTableBySearch(this.auditTrailData, ['action', 'user', 'field', 'old_value', 'new_value'])
    }
  },
  watch: {
    activeTab() {
      this.currentPage = 1
    }
  },
  async created() {
    this.remapRemovedBookingTabQuery()
    await Promise.all([
      this.loadClients(),
      this.loadData()
    ])
  },
  methods: {
    remapRemovedBookingTabQuery() {
      const tab = String(this.$route?.query?.tab || '').toLowerCase()
      if (tab === 'booking') {
        this.activeTab = 0
        const query = { ...this.$route.query }
        delete query.tab
        this.$router.replace({ query }).catch(() => {})
      }
    },
    setBookingQuickFilter(value) {
      this.filters.bookingStatus = value
      this.applyFilters()
    },
    async loadClients() {
      try {
        const res = await clientService.getClients()
        if (res && res.data) {
          const arr = Array.isArray(res.data) ? res.data : []
          this.clients = arr.map(c => (typeof c === 'string' ? { title: c } : { title: c.client_name || c.name || c.title || c }))
        } else if (res && Array.isArray(res)) {
          this.clients = res.map(c => (typeof c === 'string' ? { title: c } : { title: c.client_name || c.name || c.title || c }))
        } else {
          this.clients = []
        }
      } catch (error) {
        console.error('[InvoiceReconciliation] Error loading clients:', error)
        this.clients = []
      }
    },
    async loadData() {
      this.loading = true
      this.loadError = ''
      this.usingFallback = false
      try {
        const startDate = this.filters.startDate || ''
        const endDate = this.filters.endDate || ''
        const status = this.filters.status || ''
        const client = this.filters.client || ''
        const category = this.filters.category || ''
        const dateType = 'invoice_date'

        const res = await invoiceService.getAll(startDate, endDate, status, client, category, dateType)

        if (res && res.status === 'OK') {
          const invoices = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
          let dataArray = invoices

          if (this.filters.bookingStatus) {
            dataArray = dataArray.filter(item => (item.booking_status ?? 'Pending') === this.filters.bookingStatus)
          }

          const statistics = this.computeStatisticsFromInvoices(dataArray)

          let auditTrail = this.buildAuditTrailFromInvoices(dataArray)
          try {
            const reconRes = await enhancedInvoiceService.getReconciliationData({
              startDate,
              endDate,
              status,
              client,
              category,
              bookingStatus: this.filters.bookingStatus
            })
            if (reconRes?.data?.auditTrail && Array.isArray(reconRes.data.auditTrail) && reconRes.data.auditTrail.length > 0) {
              auditTrail = reconRes.data.auditTrail
            }
          } catch (_) {
            // Use invoice-derived audit trail
          }

          this.applyReconciliationData(dataArray, statistics, auditTrail)
          console.log('[InvoiceReconciliation] Data loaded from invoice API', { count: dataArray.length })
        } else if (res && (res.status === 401 || res.status === 403)) {
          console.warn('[InvoiceReconciliation] Session expired, redirecting to login')
          window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
          localStorage.clear()
          this.$router.push({ name: 'auth-login' })
          this.applyReconciliationData([], {}, [])
        } else {
          const reconRes = await enhancedInvoiceService.getReconciliationData({
            startDate,
            endDate,
            status,
            client,
            category: this.filters.category,
            bookingStatus: this.filters.bookingStatus
          })
          if (reconRes && (reconRes.status === 'OK' || reconRes.status === 200) && reconRes.data) {
            const { invoices = [], statistics = {}, auditTrail = [] } = reconRes.data
            let dataArray = Array.isArray(invoices) ? invoices : []
            if (this.filters.bookingStatus) {
              dataArray = dataArray.filter(item => (item.booking_status ?? 'Pending') === this.filters.bookingStatus)
            }
            this.applyReconciliationData(dataArray, statistics, auditTrail)
          } else {
            this.applyReconciliationData([], {}, [])
            this.loadError = 'No reconciliation data returned for the selected filters.'
          }
        }
      } catch (error) {
        console.error('[InvoiceReconciliation] Error loading data:', error)
        try {
          const fallback = await enhancedInvoiceService.getReconciliationDummyData()
          const d = fallback?.data || {}
          this.applyReconciliationData(d.invoices || [], d.statistics || {}, d.auditTrail || [])
          this.usingFallback = true
        } catch (e) {
          this.applyReconciliationData([], {}, [])
          this.loadError = (error && error.message) || 'Invoice API unavailable. Adjust filters or try again.'
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Info',
            text: 'Using fallback data (invoice API unavailable)',
            icon: 'InfoIcon',
            variant: 'info'
          }
        })
      } finally {
        this.loading = false
      }
    },
    computeStatisticsFromInvoices(invoices) {
      const data = Array.isArray(invoices) ? invoices : []
      const totalRevenue = parseFloat(data.reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || parseFloat(inv.amount_with_gst) || parseFloat(inv.base_amount) || 0), 0).toFixed(2))
      const paidAmount = parseFloat(data
        .filter(inv => (inv.status || '').toLowerCase() === 'paid')
        .reduce((sum, inv) => sum + (parseFloat(inv.paid_amount) || 0), 0).toFixed(2))
      const pendingAmount = parseFloat(data.reduce((sum, inv) => {
        const total = parseFloat(inv.total_amount) || parseFloat(inv.amount_with_gst) || parseFloat(inv.base_amount) || 0
        const paid = parseFloat(inv.paid_amount) || 0
        return sum + Math.max(0, total - paid)
      }, 0).toFixed(2))
      const overdueInvoices = data.filter(inv => this.isOverdue(inv.due_date || inv.dueDate)).length
      const bookedInvoices = data.filter(inv => (inv.booking_status || '').toLowerCase() === 'booked').length
      const notBookedInvoices = data.filter(inv => {
        const s = (inv.booking_status || 'Pending').toLowerCase()
        return s === 'not booked' || s === 'notbooked'
      }).length
      const eInvoiceFiled = data.filter(inv => !!inv.gstr1_filed || !!inv.einvoice_filed).length

      return {
        totalInvoices: data.length,
        totalRevenue,
        pendingAmount,
        paidAmount,
        overdueInvoices,
        bookedInvoices,
        notBookedInvoices,
        eInvoiceFiled
      }
    },
    buildAuditTrailFromInvoices(invoices) {
      const data = Array.isArray(invoices) ? invoices : []
      const entries = []
      data.forEach(inv => {
        const invId = inv.invoice_id || inv.invoice_number || inv.id || ''
        if (inv.created_at) {
          entries.push({
            timestamp: inv.created_at,
            action: 'Created',
            user: inv.added_by || '',
            field: 'invoice',
            old_value: '',
            new_value: invId
          })
        }
        if (inv.updated_at && inv.updated_at !== inv.created_at) {
          entries.push({
            timestamp: inv.updated_at,
            action: 'Updated',
            user: inv.updated_by || inv.added_by || '',
            field: 'invoice',
            old_value: '',
            new_value: invId
          })
        }
      })
      return entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    },
    isOverdue(dueDate) {
      if (!dueDate) return false
      try {
        return moment(dueDate).isBefore(moment(), 'day')
      } catch (_) {
        return false
      }
    },
    filterTableBySearch(items, fields) {
      const q = (this.searchQuery || '').toLowerCase().trim()
      if (!q) return items
      const arr = Array.isArray(items) ? items : []
      return arr.filter(item => {
        const searchable = fields.map(f => String(item[f] ?? '')).join(' ').toLowerCase()
        return searchable.includes(q)
      })
    },
    applyReconciliationData(invoices, statistics, auditTrail) {
      const data = Array.isArray(invoices) ? invoices : []
      const stats = statistics || {}

      this.statistics = {
        totalInvoices: stats.totalInvoices ?? 0,
        totalRevenue: parseFloat((stats.totalRevenue ?? 0).toFixed(2)),
        pendingAmount: parseFloat((stats.pendingAmount ?? 0).toFixed(2)),
        paidAmount: parseFloat((stats.paidAmount ?? 0).toFixed(2)),
        overdueInvoices: stats.overdueInvoices ?? 0,
        bookedInvoices: stats.bookedInvoices ?? 0,
        notBookedInvoices: stats.notBookedInvoices ?? 0,
        eInvoiceFiled: stats.eInvoiceFiled ?? 0
      }

      this.invoiceOverviewData = data.map(item => ({
        ...item,
        id: item.id ?? item.invoice_id ?? item.invoice_number,
        invoice_id: item.invoice_id || item.invoice_number || '',
        invoice_date: item.invoice_date || item.invoiceDate || '',
        client: item.client || item.client_name || '',
        total_amount: parseFloat(item.total_amount || item.amount_with_gst || 0),
        booking_status: item.booking_status ?? 'Pending'
      }))

      this.financialData = data.map(item => ({
        id: item.id ?? item.invoice_id ?? item.invoice_number,
        invoice_id: item.invoice_id || item.invoice_number || '',
        invoice_date: item.invoice_date || '',
        client: item.client || item.client_name || '',
        base_amount: parseFloat(item.base_amount || 0),
        total_amount: parseFloat(item.total_amount || item.amount_with_gst || 0),
        paid_amount: parseFloat(item.paid_amount || 0),
        pending_amount: parseFloat(item.pending_amount ?? Math.max(0, (item.total_amount || item.amount_with_gst || 0) - (item.paid_amount || 0)))
      }))

      this.taxData = data.map(item => ({
        id: item.id ?? item.invoice_id ?? item.invoice_number,
        invoice_id: item.invoice_id || item.invoice_number || '',
        invoice_date: item.invoice_date || '',
        gst_type: item.gst_type || '',
        cgst_amount: parseFloat(item.cgst_amount || item.cgst || 0),
        sgst_amount: parseFloat(item.sgst_amount || item.sgst || 0),
        igst_amount: parseFloat(item.igst_amount || item.igst || 0),
        total_gst: parseFloat(item.gst || 0),
        tds_amount: parseFloat(item.tds_amount || 0)
      }))

      this.complianceData = data.map(item => ({
        id: item.id ?? item.invoice_id ?? item.invoice_number,
        invoice_id: item.invoice_id || item.invoice_number || '',
        invoice_date: item.invoice_date || '',
        client_gst: item.client_gst || '',
        sattva_gst: item.sattva_gst || '',
        einvoice_filed: !!item.einvoice_filed,
        einvoice_date: item.einvoice_date || '',
        tds_applicable: !!item.tds_applicable,
        tds_percentage: item.tds_percentage || ''
      }))

      this.auditTrailData = Array.isArray(auditTrail) ? auditTrail : []
    },
    applyFilters() {
      this.currentPage = 1
      this.loadData()
    },
    clearFilters() {
      this.searchQuery = ''
      this.filtersExpanded = false
      this.filters = {
        startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        status: null,
        client: null,
        category: null,
        bookingStatus: null
      }
      this.applyFilters()
    },
    showExportModal() {
      if (this.$refs.exportModal) {
        this.$refs.exportModal.show()
      } else {
        console.error('ExportModal ref not found')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Export modal not available. Please refresh the page.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      }
    },
    viewInvoiceDetails(item) {
      const invoiceId = resolveInvoiceApiKey(item)
      if (!item || !invoiceId) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Invalid invoice data. Cannot view details.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }
      this.$router.push({ name: 'apps-update-invoice', params: { id: invoiceId } })
    },
    updateBookingStatus(item) {
      if (!item) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Invalid invoice data. Cannot update booking status.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }
      const invoiceKey = item.invoice_id || item.invoice_number || item.invoiceId || item.id || null
      if (!invoiceKey) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Invalid invoice data. Cannot update booking status.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }
      this.selectedInvoice = {
        ...item,
        invoice_id: item.invoice_id || item.invoice_number || item.invoiceId || invoiceKey,
        invoice_number: item.invoice_number || item.invoice_id || invoiceKey,
        id: item.id
      }
      if (this.$refs.bookingStatusModal) {
        this.$nextTick(() => {
          this.$refs.bookingStatusModal.show()
        })
      } else {
        console.error('BookingStatusModal ref not found')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Booking status modal not available. Please refresh the page.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      }
    },
    handleBookingStatusUpdated() {
      this.loadData()
    },

    openEInvoiceModal(item) {
      this.$nextTick(() => {
        if (this.$refs.eInvoiceStatusModal) this.$refs.eInvoiceStatusModal.show(item)
      })
    },

    async saveEInvoiceStatus({ invoice, payload }) {
      try {
        const invoiceId = invoice.invoice_id || invoice.id
        await enhancedInvoiceService.updateEInvoiceFilingStatus(invoiceId, payload.status, {
          filing_date: payload.filingDate,
          reason: payload.reason,
          comments: payload.comments
        })
        const inv = this.complianceData.find(i => i.invoice_id === invoiceId || i.id === invoiceId)
        if (inv) {
          inv.einvoice_filed = payload.status
          inv.einvoice_date = payload.filingDate || null
        }
        this.$bvToast.toast(
          `E-Invoice status updated to "${payload.status ? 'Filed' : 'Not Filed'}" for ${invoiceId}.`,
          { title: 'E-Invoice Updated', variant: payload.status ? 'success' : 'warning', solid: true }
        )
      } catch (err) {
        const msg = (err && err.message) || 'Failed to update e-invoice status.'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      }
    }
  }
}
</script>

<style lang="scss">
$inv-teal: #0099AC;
$inv-teal-dark: #0f6f7a;
$inv-border: #e8ecf0;
$inv-muted: #64748b;
$inv-bg: #f7f9fb;

.recon-dashboard {
  --inv-teal: #{$inv-teal};
  --inv-border: #{$inv-border};
  --inv-muted: #{$inv-muted};
  --inv-bg: #{$inv-bg};
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;

  /* Guard: feather SVGs must never paint at viewport scale (huge black X/chevron overlays). */
  svg.feather {
    max-width: 2rem;
    max-height: 2rem;
    flex-shrink: 0;
    overflow: visible;
  }
}

.recon-dash-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.65rem;

  &__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
    margin-left: auto;
  }

  .btn {
    min-height: 2.5rem;
  }

  .btn-icon {
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    svg.feather {
      width: 15px;
      height: 15px;
      max-width: 15px;
      max-height: 15px;
    }
  }

  .btn-primary,
  .btn-outline-primary {
    border-color: $inv-teal;
    color: $inv-teal-dark;

    &:hover,
    &:focus {
      background-color: rgba($inv-teal, 0.1);
      border-color: darken($inv-teal, 4%);
      color: darken($inv-teal-dark, 4%);
    }
  }

  .btn-outline-primary {
    background: transparent;
  }
}

.recon-period-label {
  display: inline-flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 600;
  color: $inv-muted;

  .feather,
  svg {
    color: $inv-teal;
  }
}

.recon-source-badge {
  font-size: 0.68rem;
  font-weight: 650;
}

.recon-spin {
  animation: recon-spin 0.8s linear infinite;
}

@keyframes recon-spin {
  to { transform: rotate(360deg); }
}

.recon-filter-toolbar {
  background: #fff;
  border: 1px solid $inv-border;
  border-radius: 10px;
  padding: 0.75rem 0.85rem 0.65rem;
}

.recon-filter-toolbar__primary {
  display: grid;
  grid-template-columns: repeat(2, minmax(110px, 0.9fr)) minmax(160px, 1.4fr) auto;
  gap: 0.5rem 0.65rem;
  align-items: end;

  @media (max-width: 767.98px) {
    grid-template-columns: 1fr 1fr;
  }
}

.recon-filter-field {
  min-width: 0;

  > label {
    display: block;
    margin-bottom: 0.15rem;
    font-size: 0.68rem;
    font-weight: 650;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $inv-muted;
  }

  .form-control,
  .form-control-sm,
  .custom-select,
  .custom-select-sm {
    min-height: 2.5rem;
    font-size: 0.82rem;
  }

  .btn {
    min-height: 2.5rem;
  }

  .btn-primary {
    background-color: $inv-teal;
    border-color: $inv-teal;

    &:hover,
    &:focus {
      background-color: darken($inv-teal, 6%);
      border-color: darken($inv-teal, 6%);
    }
  }

  &--actions {
    @media (max-width: 767.98px) {
      grid-column: 1 / -1;
    }
  }
}

.recon-vselect {
  .vs__dropdown-toggle {
    min-height: 2.5rem;
    border-color: $inv-border;
  }
}

.recon-filter-count {
  font-size: 0.65rem;
  vertical-align: middle;
}

.recon-booking-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.recon-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  border: 1px solid $inv-border;
  background: $inv-bg;
  color: #334155;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1;
  min-height: 2.25rem;
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    border-color: rgba($inv-teal, 0.4);
    color: $inv-teal-dark;
  }

  &.is-active {
    background: rgba($inv-teal, 0.12);
    border-color: rgba($inv-teal, 0.45);
    color: $inv-teal-dark;
  }

  &__count {
    min-width: 1.15rem;
    padding: 0.12rem 0.35rem;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.06);
    font-size: 0.65rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: inherit;
  }

  &.is-active &__count {
    background: rgba($inv-teal, 0.16);
  }
}

.recon-filters-advanced {
  margin-top: 0.65rem;
  padding-top: 0.65rem;
  border-top: 1px dashed $inv-border;

  .form-group > label,
  .col-form-label {
    margin-bottom: 0.15rem;
    font-size: 0.68rem;
    font-weight: 650;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $inv-muted;
  }
}

.recon-alert {
  border-radius: 10px;
  border: 1px solid rgba(194, 59, 59, 0.2);
}

.recon-workspace {
  border: 1px solid $inv-border !important;
  border-radius: 12px !important;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.04) !important;
  overflow: hidden;
  max-width: 100%;
}

.recon-workspace__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.recon-workspace__search {
  flex: 1 1 220px;
  max-width: 360px;
  min-width: 0;
}

.recon-search-icon {
  background: #fff;
  border-color: $inv-border;
}

.recon-workspace__meta {
  font-size: 0.75rem;
  font-weight: 600;
  color: $inv-muted;
}

.recon-dash-tabs {
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-bottom: 1px solid $inv-border !important;

  .nav-link {
    white-space: nowrap;
    font-size: 0.8125rem;
    font-weight: 600;
    color: $inv-muted;
    border: 0 !important;
    border-bottom: 2px solid transparent !important;
    border-radius: 0;
    padding: 0.65rem 0.85rem;

    &:hover {
      color: $inv-teal-dark;
    }

    &.active {
      color: $inv-teal-dark !important;
      background: transparent !important;
      border-bottom-color: $inv-teal !important;
    }
  }
}
</style>
